// Color-coded status chip — port of cardrive/admin-shell.jsx StatusPill.
// Single source of truth for the color → status mapping; consumers pass
// any known status key (with a fallback to the raw string + neutral grey
// for unknown values).

const STATUS_MAP: Record<string, [fg: string, bg: string, label: string]> = {
  // bookings
  requested: ['#92400e', '#fef3c7', 'Requested'],
  confirmed: ['#1e40af', '#dbeafe', 'Confirmed'],
  active: ['#065f46', '#d1fae5', 'Active'],
  completed: ['#374151', '#f3f4f6', 'Completed'],
  cancelled: ['#6b7280', '#f3f4f6', 'Cancelled'],
  disputed: ['#9f1239', '#ffe4e6', 'Disputed'],
  refunded: ['#5b21b6', '#ede9fe', 'Refunded'],
  // cars / listings
  live: ['#065f46', '#d1fae5', 'Live'],
  paused: ['#92400e', '#fef3c7', 'Paused'],
  review: ['#9a3412', '#ffedd5', 'In review'],
  rejected: ['#9f1239', '#ffe4e6', 'Rejected'],
  approved: ['#065f46', '#d1fae5', 'Approved'],
  pending: ['#92400e', '#fef3c7', 'Pending'],
  // KYC / verification
  verified: ['#065f46', '#d1fae5', 'Verified'],
  // payouts
  paid: ['#065f46', '#d1fae5', 'Paid'],
  in_transit: ['#1e40af', '#dbeafe', 'In transit'],
  on_hold: ['#9f1239', '#ffe4e6', 'On hold'],
  // disputes — unified table (apps/api/src/modules/disputes/transitions.ts)
  evidence_pending: ['#92400e', '#fef3c7', 'Evidence pending'],
  under_review: ['#1e40af', '#dbeafe', 'Under review'],
  escalated_to_carrier: ['#9a3412', '#ffedd5', 'Escalated to carrier'],
  withdrawn: ['#6b7280', '#f3f4f6', 'Withdrawn'],
  resolved: ['#065f46', '#d1fae5', 'Resolved'],
  // dispute resolutions
  partial_refund: ['#5b21b6', '#ede9fe', 'Partial refund'],
  no_action: ['#374151', '#f3f4f6', 'No action'],
  vendor_charged: ['#9a3412', '#ffedd5', 'Vendor charged'],
  customer_charged: ['#9a3412', '#ffedd5', 'Customer charged'],
  // claims (legacy keys retained for any callers still passing these)
  awaiting_evidence: ['#92400e', '#fef3c7', 'Awaiting evidence'],
  pending_review: ['#1e40af', '#dbeafe', 'Pending review'],
  in_review: ['#1e40af', '#dbeafe', 'In review'],
  // promos
  scheduled: ['#1e40af', '#dbeafe', 'Scheduled'],
  ended: ['#6b7280', '#f3f4f6', 'Ended'],
  // complaints
  open: ['#9a3412', '#ffedd5', 'Open'],
  in_progress: ['#1e40af', '#dbeafe', 'In progress'],
  closed: ['#374151', '#f3f4f6', 'Closed'],
  breached: ['#9f1239', '#ffe4e6', 'SLA breached'],
  // moderation / onboarding
  docs: ['#92400e', '#fef3c7', 'Docs needed'],
  complete: ['#065f46', '#d1fae5', 'Complete'],
  // SLA priority
  urgent: ['#9f1239', '#ffe4e6', 'Urgent'],
  high: ['#9a3412', '#ffedd5', 'High'],
  med: ['#1e40af', '#dbeafe', 'Med'],
  low: ['#374151', '#f3f4f6', 'Low'],
  // user states
  invited: ['#92400e', '#fef3c7', 'Invited'],
  disabled: ['#6b7280', '#f3f4f6', 'Disabled'],
  // fixed-price crew opt-in daily badge (crew list)
  prices_confirmed: ['#065f46', '#d1fae5', 'Prices confirmed today'],
  prices_stale: ['#9f1239', '#ffe4e6', 'Not confirmed today'],
};

export interface StatusPillProps {
  status: string;
  size?: 'sm' | 'md';
}

export function StatusPill({ status, size = 'sm' }: StatusPillProps) {
  const entry = STATUS_MAP[status] ?? ['#374151', '#f3f4f6', status];
  const [fg, bg, label] = entry;
  const py = size === 'sm' ? 'py-0.5' : 'py-1';
  const fs = size === 'sm' ? 'text-[11px]' : 'text-[12.5px]';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 ${py} ${fs} font-medium`}
      style={{ color: fg, background: bg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: fg }} />
      {label}
    </span>
  );
}
